import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../theme/ThemeContext';
import { useFocusDialog } from '../../../hooks/useFocusDialog';

export type PhoneCalendarView = 'schedule' | 'day' | 'threeDay' | 'month';

const VIEW_OPTIONS: Array<{ view: PhoneCalendarView; labelKey: string }> = [
  { view: 'schedule', labelKey: 'responsive.calendar.schedule' },
  { view: 'day', labelKey: 'responsive.calendar.day' },
  { view: 'threeDay', labelKey: 'responsive.calendar.threeDays' },
  { view: 'month', labelKey: 'responsive.calendar.month' }
];

interface PhoneCalendarSheetProps {
  open: boolean;
  view: string;
  onClose: () => void;
  onViewChange: (view: PhoneCalendarView) => void;
  children?: React.ReactNode;
}

/** Bottom sheet with the view switcher plus the calendar filters and actions. */
export const PhoneCalendarSheet: React.FC<PhoneCalendarSheetProps> = ({ open, view, onClose, onViewChange, children }) => {
  const { t } = useTranslation();
  const { tokens } = useTheme();
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusDialog(open, panelRef, onClose);

  if (!open) return null;

  return (
    <>
      <div className="phone-cal-sheet-backdrop" onClick={onClose} />
      <div
        ref={panelRef}
        className="phone-cal-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={t('responsive.calendar.views')}
        data-testid="phone-calendar-sheet"
        style={{ background: tokens.app.surface, color: tokens.app.text }}
      >
        <div className="phone-cal-sheet-handle" style={{ background: tokens.app.border }} />
        <div className="phone-cal-sheet-views" role="radiogroup" aria-label={t('responsive.calendar.views')}>
          {VIEW_OPTIONS.map(option => {
            const active = option.view === view;
            return (
              <button
                key={option.view}
                type="button"
                role="radio"
                aria-checked={active}
                data-testid={`phone-view-${option.view}`}
                onClick={() => { onViewChange(option.view); onClose(); }}
                style={{
                  background: active ? `${tokens.app.primary}2e` : 'transparent',
                  color: active ? tokens.app.primary : tokens.app.text,
                  fontWeight: active ? 700 : 500
                }}
              >
                {t(option.labelKey)}
              </button>
            );
          })}
        </div>
        <hr style={{ border: 0, borderTop: `1px solid ${tokens.app.border}`, margin: '8px 0' }} />
        {children}
        <button type="button" className="phone-cal-sheet-close" onClick={onClose} style={{ color: tokens.app.primary }}>
          {t('responsive.calendar.close')}
        </button>
      </div>
    </>
  );
};
